// =============================================================================
// MeuDriver — Agenda Week Grid (calendar visualization)
// =============================================================================

import { CalendarOff } from 'lucide-react'
import { format, isToday, isSameDay, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { AvailabilityExceptionRow, BookingRow, ProfileRow } from '@/types/database'

export type BookingWithStudent = BookingRow & {
  student?: Pick<ProfileRow, 'full_name'>
}

export interface DayAvailability {
  enabled: boolean
  start_time: string
  end_time: string
}

export type WeekAvailability = Record<number, DayAvailability>

const HOUR_START = 8
const HOUR_END = 20
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i)

function timeToHours(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h + (m ?? 0) / 60
}

function bookingStyle(scheduledAt: string, durationMinutes: number) {
  const date = parseISO(scheduledAt)
  const startHour = date.getHours() + date.getMinutes() / 60
  const totalHours = HOUR_END - HOUR_START
  const top = ((startHour - HOUR_START) / totalHours) * 100
  const height = (durationMinutes / 60 / totalHours) * 100
  return { top: `${Math.max(0, top)}%`, height: `${Math.min(height, 100 - Math.max(0, top))}%` }
}

function availabilityStyle(startTime: string, endTime: string) {
  const startHour = timeToHours(startTime)
  const endHour = timeToHours(endTime)
  const totalHours = HOUR_END - HOUR_START
  const top = ((startHour - HOUR_START) / totalHours) * 100
  const height = ((endHour - startHour) / totalHours) * 100
  return { top: `${Math.max(0, top)}%`, height: `${Math.min(height, 100 - Math.max(0, top))}%` }
}

interface Props {
  weekDays: Date[]
  availability: WeekAvailability
  bookings: BookingWithStudent[]
  exceptions: AvailabilityExceptionRow[]
  loading: boolean
}

export function AgendaWeekGrid({ weekDays, availability, bookings, exceptions, loading }: Props) {
  function bookingsForDay(day: Date) {
    return bookings.filter((b) => isSameDay(parseISO(b.scheduled_at), day))
  }
  function dayOfWeekIndex(day: Date) {
    return day.getDay()
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="grid border-b" style={{ gridTemplateColumns: '4rem repeat(7, 1fr)' }}>
              <div className="py-3" />
              {weekDays.map((day) => (
                <div key={day.toISOString()} className={cn('py-3 px-2 text-center border-l', isToday(day) && 'bg-teal-50')}>
                  <p className="text-xs font-medium text-muted-foreground uppercase">{format(day, 'EEE', { locale: ptBR })}</p>
                  <p className={cn('text-sm font-semibold mt-0.5', isToday(day) ? 'text-teal-600' : 'text-gray-900')}>
                    {format(day, 'd')}
                  </p>
                </div>
              ))}
            </div>

            <div
              className="relative grid"
              style={{ gridTemplateColumns: '4rem repeat(7, 1fr)', height: `${HOURS.length * 56}px` }}
            >
              <div className="relative">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="absolute w-full flex items-start justify-end pr-2"
                    style={{ top: `${((h - HOUR_START) / (HOUR_END - HOUR_START)) * 100}%`, height: `${100 / HOURS.length}%` }}
                  >
                    <span className="text-[10px] text-muted-foreground -mt-2">{h}h</span>
                  </div>
                ))}
              </div>

              {weekDays.map((day) => {
                const dow = dayOfWeekIndex(day)
                const avail = availability[dow]
                const dayBookings = bookingsForDay(day)
                const isException = exceptions.some(
                  (e) => !e.is_available && e.date === format(day, 'yyyy-MM-dd'),
                )

                return (
                  <div
                    key={day.toISOString()}
                    className={cn('relative border-l border-t border-gray-100', isToday(day) && 'bg-teal-50/30')}
                  >
                    {HOURS.map((h) => (
                      <div
                        key={h}
                        className="absolute w-full border-t border-gray-100"
                        style={{ top: `${((h - HOUR_START) / (HOUR_END - HOUR_START)) * 100}%` }}
                      />
                    ))}

                    {avail.enabled && !isException && (
                      <div
                        className="absolute inset-x-0 bg-green-100/60 border-l-2 border-green-400 pointer-events-none"
                        style={availabilityStyle(avail.start_time, avail.end_time)}
                      />
                    )}

                    {isException && (
                      <div className="absolute inset-0 bg-red-50/60 flex items-center justify-center">
                        <CalendarOff className="h-4 w-4 text-red-300" />
                      </div>
                    )}

                    {dayBookings.map((booking) => {
                      const style = bookingStyle(booking.scheduled_at, booking.duration_minutes)
                      const studentName = booking.student?.full_name ?? 'Aluno'
                      return (
                        <div
                          key={booking.id}
                          className="absolute inset-x-0.5 rounded bg-teal-500 text-white text-[10px] font-medium px-1 py-0.5 overflow-hidden cursor-pointer hover:bg-teal-600 transition-colors z-10"
                          style={style}
                          title={`${studentName} — ${booking.duration_minutes} min`}
                        >
                          <p className="truncate leading-tight">{studentName}</p>
                          <p className="opacity-80">{booking.duration_minutes}min</p>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
